import { Button, Select } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { convertArea, lengthToDegrees, polygon } from '@turf/turf';
import { isAxiosError } from 'axios';
import type { Polygon } from 'geojson';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useAsync } from 'react-use';
import { useSnapshot } from 'valtio';
import { generateProposalPacket, getProject } from '~/api/projects2';
import { getGridsV4 } from '~/common/griddingUtils';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { defaultBasemapStyle } from '~/common/types';
import { SendEmailModal } from '~/components2/SendEmailModal';
import { calculateArea } from '~/helpers/unitHelpers';
import MultiButton from '~/pages/common/ProjectDataPanel/MultiButton';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import projectDetailsMapStore from '../map/projectDetailsMapStore';

export const gridOptions = [
  {
    label: '30 acres',
    value: 30,
  },
  {
    label: '20 acres',
    value: 20,
  },
  {
    label: '10 acres',
    value: 10,
  },
  {
    label: '5 acres',
    value: 5,
  },
];

/** Extracted from TabDetails so a failed proposal packet request can be exercised without rendering the component. */
export async function submitProposalPacket(
  project: { id: string },
  organizationId: string | undefined,
  objects: unknown,
  ccEmails?: string[],
) {
  try {
    await generateProposalPacket(project.id, objects, ccEmails);

    captureTypedPosthogEvent(POSTHOG_EVENTS.projectProposalPacketExported, {
      project_id: project.id,
      organization_id: organizationId,
      format: 'packet',
    });

    notifyExportQueued({
      message: 'The proposal packet will be emailed to you. It could take up to 15 minutes.',
      ccEmails,
    });
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectProposalPacketExportFailed, {
      project_id: project.id,
      organization_id: organizationId,
      format: 'packet',
      reason: isAxiosError(err) && err.response?.status ? String(err.response.status) : 'network_error',
    });
    notifyDownloadError();
    return false;
  }
}

export function MapChildren() {
  const { current } = useMap();
  const { currentObject, objectsAsArray, proposalGridSize } = useSnapshot(
    projectDetailsMapStore,
  ) as typeof projectDetailsMapStore;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    sortObjectAndSetTitle();
  }, [objectsAsArray.length, currentObject?.geometry]);

  function sortObjectAndSetTitle() {
    const sortedArray = sortBy(
      projectDetailsMapStore.objectsAsArray,
      (o) => (o.geometry as Polygon).coordinates[0][0][1],
    );
    let counter = 1;

    sortedArray.forEach((o) => {
      projectDetailsMapStore.newObject({
        ...o,
        title: `${counter++}`,
      });
      projectDetailsMapStore.shouldRedraw = true;
    });
  }

  return (
    <div className="group absolute right-0 top-0 z-50 mx-2 space-y-2 p-2">
      <MultiButton
        color="bg-white"
        label="Add Sub-Area"
        icon="plus"
        onClick={async () => {
          // 30 acre box, middle of the screen
          const sideLength = lengthToDegrees(Math.sqrt(convertArea(proposalGridSize, 'acres', 'meters')), 'meters');

          const { lng, lat } = current!.getCenter();

          await projectDetailsMapStore.resetCurrentObjectId();
          const id = nanoid();
          projectDetailsMapStore.newObject({
            type: 'polygon',
            geometry: polygon([
              [
                [lng, lat],
                [lng + sideLength, lat],
                [lng + sideLength, lat + sideLength],
                [lng, lat + sideLength],
                [lng, lat],
              ],
            ]).geometry,
            properties: {
              color: 'red',
              fillOpacity: 0.1,
              strokeWidth: 2,
              showTitle: true,
            },
            id,
            title: id,
            description: '',
            sortOrder: 'a1',
            interactive: {
              lines: false,
              vertices: false,
            },
          });
          //sortObjectAndSetTitle();
          projectDetailsMapStore.setCurrentObjectId(id);
        }}
      />

      {currentObject && (
        <MultiButton
          icon="delete"
          label="Delete"
          variant="danger"
          onClick={() => {
            projectDetailsMapStore.deleteObject(currentObject);
          }}
        />
      )}
    </div>
  );
}

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const _project = useAsync(async () => {
    return await getProject(projectId);
  });

  const { proposalGridSize } = useSnapshot(projectDetailsMapStore);

  const [confirmOpen, setConfirmOpen] = useState(false);

  // find the optimal grid size for initial state
  useEffect(() => {
    if (!_project.value) {
      return;
    }

    const project = _project.value!;

    const projectArea = calculateArea({
      unitSystem: 'imperial',
      unitType: 'acres',
      geometry: project.geometry,
    });

    const gridSizes = gridOptions.map((option) => option.value);

    let optimalGridSize = gridSizes[0];

    gridSizes.forEach((size) => {
      if (size > projectArea.value) {
        optimalGridSize = size;
      }
    });

    projectDetailsMapStore.setProposalGridSize(optimalGridSize);
  }, [_project]);

  // create new grids every time grid size input changes
  useEffect(() => {
    if (!_project.value) {
      return;
    }
    const project = _project.value;

    projectDetailsMapStore.reset();

    // create initial view frames
    projectDetailsMapStore.currentMap = {
      isLoading: false,
      id: 'temp',
      name: 'temp',
      image: 'temp',
      unitSystem: 'imperial',
      baseMap: defaultBasemapStyle,
      createdAt: DateTime.now(),
      createdBy: {
        name: 'me',
      },
      updatedAt: DateTime.now(),
      updatedBy: {
        name: 'me',
      },
      objects: {},
    };

    const frames = getGridsV4({
      boundary: project.geometry,
      gridSize: proposalGridSize,
      linear: project.linear,
    });

    frames.forEach((f) => {
      projectDetailsMapStore.newObject({
        type: 'polygon',
        geometry: f.geometry,
        properties: {
          color: 'red',
          fillOpacity: 0.1,
          strokeWidth: 2,
          showTitle: true,
          ...(!!f.properties && { ...f.properties }),
        },
        id: f.id as string,
        title: f.id as string,
        description: '',
        sortOrder: 'a1',
        interactive: {
          lines: false,
          vertices: false,
        },
      });
    });

    return () => projectDetailsMapStore.reset();
  }, [_project, proposalGridSize]);

  if (!_project.value) {
    return null;
  }

  const project = _project.value!;

  return (
    <div className="flex flex-col space-y-4">
      <div className="mr-10 space-y-6">
        <div className="text-base italic">Get a leg up for your next proposal.</div>
        <div className="text-sm">
          Generate a proposal-ready Word document (8.5x11 landscape) with detailed utility maps and utility provider
          lists to help inform your next proposal. Feel free to use the Word document output however you would like!
        </div>
        <div className="text-sm text-blue-50 underline hover:text-blue-60">
          <a
            href="https://civilgrid.help.usepylon.com/articles/5843406317-creating-a-proposal-packet"
            target="_blank"
            rel="noreferrer noopener"
            className="text-blue-50 underline hover:text-blue-60"
          >
            View sample
          </a>
        </div>
        <div className="text-sm">
          <div className="mb-2">Steps for generating your CivilGrid Proposal Packet:</div>
          <ol className="list-inside list-decimal space-y-2">
            <li>
              Check to make sure your{' '}
              <a
                target="_blank"
                href="https://civilgrid.help.usepylon.com/articles/2112325516-uploading-company-logo-to-your-civilgrid-organization"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                company logo
              </a>{' '}
              is uploaded to your CivilGrid organization, as this will be automatically included in your packet.
            </li>
            <li>
              Check and adjust your sub-areas. Each subarea will receive its own page with a figure and utility provider
              list in the packet. If your project exceeds a certain size, we have already automatically generated
              sub-areas for you. Please verify the distribution of sub-areas to the left. You can add/remove, and drag
              around each sub-area so that each one displays optimally.
            </li>
            <li>Click Generate Proposal Packet.</li>
            <li>
              It’ll take a few minutes for us to generate the Proposal Packet. It will be delivered to your inbox when
              completed.
            </li>
            <li>
              Are you including use of CivilGrid as part of your proposal? We’ve created{' '}
              <a
                target="_blank"
                href="https://civilgrid.help.usepylon.com/articles/1374706450-about-civilgrid-info-for-rfps-and-proposals"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                reference materials
              </a>{' '}
              that you can use, including company info (SBE certificate, NAICS code, etc.), product information, and
              team bios.
            </li>
          </ol>
        </div>
      </div>

      <div className="w-[200px]">
        <Select
          label="Select Sub Area Size (Acres)"
          options={gridOptions.map((option) => ({ label: option.label, value: String(option.value) }))}
          value={String(proposalGridSize)}
          onValueChange={(e) => e && projectDetailsMapStore.setProposalGridSize(Number(e))}
          data-testid="proposal-packet-grid-size-select"
        />
      </div>
      <div>
        <Button data-testid="proposal-packet-generate-button" onClick={() => setConfirmOpen(true)}>
          Generate Proposal Packet
        </Button>
      </div>
      <SendEmailModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Generate Proposal Packet"
        testIdPrefix="proposal-packet"
        onSend={(ccEmails) => {
          const { currentMap } = projectDetailsMapStore;
          if (currentMap.isLoading) return false;
          return submitProposalPacket(
            project,
            rootStore.currentOrganizationId || undefined,
            currentMap.objects,
            ccEmails.length > 0 ? ccEmails : undefined,
          );
        }}
      />
    </div>
  );
}
